import type { ReactNode } from "react";
import type { Meta } from "../lib/types.ts";
import { titleCase } from "../lib/hooks.ts";

export function Badge({ value, label, title }: { value: string; label?: string; title?: string }) {
  const text = titleCase(label ?? value);
  return (
    <span className={`badge ${value}`} title={title ?? text}>
      <span className="dot" />
      {text}
    </span>
  );
}

export function Card({
  title,
  action,
  children,
  tight,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  tight?: boolean;
}) {
  return (
    <section className="card">
      {title ? (
        <header className="card-head">
          <h2>{title}</h2>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>{action}</div>
        </header>
      ) : null}
      <div className={`card-body${tight ? " tight" : ""}`}>{children}</div>
    </section>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="card stat">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function Spinner() {
  return <span className="spinner" aria-label="carregando" />;
}

export function Empty({ children = "Nenhum registro encontrado." }: { children?: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="modal-head">
          <h2>{title}</h2>
          <button className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={onClose}>
            Fechar
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

export function Pager({
  meta,
  page,
  onPage,
}: {
  meta: Meta | null;
  page: number;
  onPage: (page: number) => void;
}) {
  if (!meta) return null;
  const from = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const to = Math.min(meta.total, meta.page * meta.limit);
  return (
    <div className="pager">
      <span>
        Exibindo <strong>{from}–{to}</strong> de <strong>{meta.total}</strong>
      </span>
      <span className="spacer" />
      <button className="btn sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Anterior
      </button>
      <span>
        Página <strong>{meta.page}</strong> de <strong>{meta.totalPages}</strong>
      </span>
      <button className="btn sm" disabled={page >= meta.totalPages} onClick={() => onPage(page + 1)}>
        Próxima
      </button>
    </div>
  );
}

export function Bar({ label, value, total }: { label: string; value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="bar-row">
      <span className="subtle">{label}</span>
      <span className="bar-track">
        <span className="bar-fill" style={{ width: `${pct}%` }} />
      </span>
      <strong style={{ textAlign: "right" }}>{value}</strong>
    </div>
  );
}

export function ErrorAlert({ message }: { message: string }) {
  return <div className="alert error">{message}</div>;
}
